/**
 * The Gaps — unaligned work and unserved objectives, plus whatever Compass
 * already found. Findings are accepted/dismissed through the canonical
 * findings store; this screen never runs the detector itself.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, Compass, Link2, X } from 'lucide-react';
import { Select } from '@/components/ds';
import { useUserStore } from '@/stores/prioritization/userStore';
import { COMPASS_AGENT_TYPE } from '@/lib/intelligence/compassAnalyzer';
import type { AlignmentGap } from '@/lib/strategyAlignment';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, SecondaryButton, StatusChip } from '../components/primitives';
import { useAlignment, useCompassFindings } from './useAlignment';
import { linkItemToObjective, linkableObjectives } from './alignLinking';

const GAP_COPY: Record<AlignmentGap['type'], string> = {
  unmapped_priority: 'Work with no objective behind it',
  unsupported_objective: 'An objective nobody is working on',
  weak_alignment: 'Only loosely connected',
};

const SEVERITY_TONE: Record<AlignmentGap['severity'], 'coral' | 'mustard' | 'neutral'> = {
  high: 'coral',
  medium: 'mustard',
  low: 'neutral',
};

export default function AlignGapsScreen() {
  const app = getAppDefinition('align')!;
  const navigate = useNavigate();
  const { loading, error, workspaceId, report, reload } = useAlignment();
  const { findings, accept, dismiss } = useCompassFindings();
  const userId = useUserStore((s) => s.userId);
  const [busyId, setBusyId] = useState<string | null>(null);
  // A refused accept/dismiss leaves the finding active in the store; say why,
  // next to the finding it belongs to.
  const [actionError, setActionError] = useState<{ id: string; message: string } | null>(null);
  // Linking an unbacked item to an objective: which item has its picker open,
  // which objective is picked, and why the last attempt (if any) was refused.
  const [linkingItemId, setLinkingItemId] = useState<string | null>(null);
  const [pickedObjectiveId, setPickedObjectiveId] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<{ id: string; message: string } | null>(null);

  const gaps = report?.gaps ?? [];
  const objectives = linkableObjectives(report?.objectives ?? []);
  const noObjectives = report?.totalObjectives === 0;

  const openLinker = (itemId: string) => {
    setLinkingItemId(itemId);
    setPickedObjectiveId('');
    setLinkError(null);
  };

  const closeLinker = () => {
    setLinkingItemId(null);
    setPickedObjectiveId('');
  };

  const link = async (itemId: string) => {
    if (!userId) {
      setLinkError({ id: itemId, message: 'Sign in again to link this item.' });
      return;
    }
    if (!pickedObjectiveId) return;
    setLinkBusy(true);
    setLinkError(null);
    const result = await linkItemToObjective({ itemId, objectiveId: pickedObjectiveId, userId });
    setLinkBusy(false);
    if (!result.ok) {
      setLinkError({ id: itemId, message: result.message });
      return;
    }
    closeLinker();
    reload();
  };

  const act = async (findingId: string, action: 'accept' | 'dismiss') => {
    if (!userId) {
      setActionError({ id: findingId, message: 'Sign in again to act on this finding.' });
      return;
    }
    setBusyId(findingId);
    setActionError(null);
    const result =
      action === 'accept'
        ? await accept(COMPASS_AGENT_TYPE, findingId, userId)
        : await dismiss(COMPASS_AGENT_TYPE, findingId, userId);
    setBusyId(null);
    if (!result.success) {
      setActionError({
        id: findingId,
        message: `Finding was not ${action === 'accept' ? 'accepted' : 'dismissed'}: ${result.error ?? 'try again.'}`,
      });
    }
  };

  return (
    <AppShell app={app} backTo="/apps/align" backLabel="Back to the map">
      <ScreenHeading
        title="The gaps"
        subtitle="Where the work and the intent have come apart — and what to do about each."
      />
      <BallLine ballWith="you — close a gap or accept it deliberately" />

      <div className="space-y-4">
        <AppCard className="!p-0">
          <div className="px-5 pt-5">
            <Eyebrow>Gaps · {gaps.length}</Eyebrow>
          </div>
          {!workspaceId ? (
            <p className="px-5 pb-5 pt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              No workspace is selected. Choose one, then come back.
            </p>
          ) : error ? (
            <p
              role="alert"
              className="px-5 pb-5 pt-2 font-body text-sm text-[var(--coral-text)]"
            >
              {error}
            </p>
          ) : loading ? (
            <p className="px-5 pb-5 pt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
              Reading…
            </p>
          ) : (
            <>
              {noObjectives ? (
                <div className="flex flex-wrap items-center gap-3 px-5 pb-4 pt-2">
                  <p className="font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                    No objectives are set yet, so there is nothing to link work to.
                  </p>
                  <SecondaryButton
                    onClick={() => navigate('/prioritization/strategy/objectives')}
                    className="!px-2.5 !py-1 !text-xs"
                  >
                    Set objectives
                  </SecondaryButton>
                </div>
              ) : null}
              {gaps.length === 0 ? (
                noObjectives ? null : (
                  <p className="px-5 pb-5 pt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                    Nothing is drifting — every objective has work behind it and every item points
                    somewhere.
                  </p>
                )
              ) : (
                <ul className="mt-2">
                  {gaps.map((gap, i) => (
                    <li
                      key={`${gap.type}-${gap.entityId}-${i}`}
                      className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle
                          className={`mt-0.5 h-4 w-4 shrink-0 ${
                            gap.severity === 'high'
                              ? 'text-luminous-coral-600'
                              : 'text-electric-mustard-600'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                            {gap.entityName}
                          </p>
                          <p className="mt-0.5 font-body text-xs text-gray-600 dark:text-[var(--text-sec)]">
                            {GAP_COPY[gap.type]} — {gap.recommendation}
                          </p>
                        </div>
                        <StatusChip label={gap.severity} tone={SEVERITY_TONE[gap.severity]} />
                      </div>
                      {gap.type === 'unmapped_priority' && objectives.length > 0 ? (
                        <div className="mt-2 pl-7">
                          {linkingItemId === gap.entityId ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <Select
                                aria-label={`Objective for ${gap.entityName}`}
                                value={pickedObjectiveId}
                                onChange={(e) => setPickedObjectiveId(e.target.value)}
                                disabled={linkBusy}
                                size="sm"
                                className="min-w-0 flex-1"
                                placeholder="Choose an objective"
                                options={objectives.map((o) => ({ value: o.id, label: o.name }))}
                              />
                              <SecondaryButton
                                onClick={() => void link(gap.entityId)}
                                disabled={linkBusy || !pickedObjectiveId}
                                className="!px-2.5 !py-1 !text-xs"
                              >
                                <Check className="h-3 w-3" /> Link
                              </SecondaryButton>
                              <SecondaryButton
                                onClick={closeLinker}
                                disabled={linkBusy}
                                className="!px-2.5 !py-1 !text-xs"
                              >
                                <X className="h-3 w-3" /> Cancel
                              </SecondaryButton>
                            </div>
                          ) : (
                            <SecondaryButton
                              onClick={() => openLinker(gap.entityId)}
                              disabled={linkBusy}
                              className="!px-2.5 !py-1 !text-xs"
                            >
                              <Link2 className="h-3 w-3" /> Link to an objective
                            </SecondaryButton>
                          )}
                          {linkError?.id === gap.entityId ? (
                            <p
                              role="alert"
                              className="mt-2 font-body text-xs text-[var(--coral-text)]"
                            >
                              {linkError.message}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </AppCard>

        <AppCard className="!p-0">
          <div className="flex items-center gap-2 px-5 pt-5">
            <Compass className="h-4 w-4 text-alpine-green-600" />
            <Eyebrow>What Compass noticed · {findings.length}</Eyebrow>
          </div>
          {findings.length === 0 ? (
            <p className="px-5 pb-5 pt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              Nothing outstanding. Findings appear here when the Compass agent runs.
            </p>
          ) : (
            <ul className="mt-2">
              {findings.map((f) => (
                <li
                  key={f.id}
                  className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                >
                  <p className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                    {f.title}
                  </p>
                  <p className="mt-0.5 font-body text-xs text-gray-600 dark:text-[var(--text-sec)]">
                    {f.summary}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <SecondaryButton
                      onClick={() => void act(f.id, 'accept')}
                      disabled={busyId === f.id}
                      className="!px-2.5 !py-1 !text-xs"
                    >
                      <Check className="h-3 w-3" /> Accept
                    </SecondaryButton>
                    <SecondaryButton
                      onClick={() => void act(f.id, 'dismiss')}
                      disabled={busyId === f.id}
                      className="!px-2.5 !py-1 !text-xs"
                    >
                      <X className="h-3 w-3" /> Dismiss
                    </SecondaryButton>
                  </div>
                  {actionError?.id === f.id ? (
                    <p role="alert" className="mt-2 font-body text-xs text-[var(--coral-text)]">
                      {actionError.message}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </AppCard>

        {workspaceId && !error && !loading && !noObjectives && gaps.length === 0 && findings.length === 0 ? (
          <EmptyState
            title="Nothing to fix right now"
            hint="Come back after the next round of work lands, or after Compass runs again."
          />
        ) : null}
      </div>
    </AppShell>
  );
}
